import { Typography, Divider } from '@material-ui/core'

import { PriceRow } from '../../../../components/PriceRow'
import { getTranslate, lang } from '../../../../localization'
import {
  BillingSummary,
  PromotionApplication,
  RoomStayForCart
} from '../../../../types/cart'
import PromotionAllocationRows from '../PromotionAllocationRows'
import useStyles from './styles/index.styles'

type PriceDetailsCardProps = {
  billingData: BillingSummary
  step: number
  showTaxText: boolean
  /** Null when the reservation has not been made yet, or the field was not returned. */
  promotionApplication?: PromotionApplication | null
  /** The original cart lines turn allocation references into useful room names. */
  roomStays?: RoomStayForCart[]
  /** The code the booker typed, so an unapplied one can be named back to them. */
  requestedPromotionCode?: string
}

const FinalBillingSummary = ({
  billingData,
  step,
  showTaxText,
  promotionApplication,
  roomStays = [],
  requestedPromotionCode
}: PriceDetailsCardProps) => {
  const classes = useStyles()
  const promotionAllocations = (promotionApplication?.applied ?? []).flatMap(
    (promotion) =>
      (promotion.allocations ?? []).map((allocation) => ({
        ...allocation,
        // Older responses omit allocation currency when it equals the promotion's. Preserve
        // that fact before handing the row to the shared renderer.
        currency: allocation.currency || promotion.currency
      }))
  )

  const allocationRoomLabel = (roomStayReference: string) => {
    const roomIndex = roomStays.findIndex(
      (room) => room.roomStayReference === roomStayReference
    )
    const room = roomStays[roomIndex]

    return room
      ? `${getTranslate('Room Stay')} ${roomIndex + 1} — ${
          room.roomTypeName
        } (${room.displayRatePlanCode})`
      : getTranslate('Room Stay')
  }

  return (
    <div className={classes.card}>
      {/*
        A code was asked for and evaluation returned nothing. This is deliberately
        distinguished from "no promotion" — a booker who typed a code and sees full
        price with no explanation has no way to tell a typo from an ineligible stay.
        The reason itself is not available: the promotion service's GetEvaluationTrace
        RPC is Unimplemented, so this says THAT, not WHY.
      */}
      {requestedPromotionCode &&
      promotionApplication &&
      promotionApplication.applied.length === 0 ? (
        <div className={classes.unappliedCode}>
          <Typography variant="body2">
            {getTranslate('Code {code} was not applied to this booking', {
              code: requestedPromotionCode
            })}
          </Typography>
        </div>
      ) : (
        <></>
      )}
      <div className={classes.topHeader}>
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Final Billing Summary')}
        </Typography>
      </div>
      <Divider />
      <div className={classes.middle}>
        {/*
          Tolerate a billingData without usable roomsPrice rather than throwing.

          This value arrives from three different setBillingData sources, one of which is
          restored from localStorage and can have been written by an older build. Blanking
          the entire reserve step -- after the reservation has already SUCCEEDED server-side
          -- is a far worse outcome than rendering the totals without the per-room lines,
          which are a breakdown of a number shown below anyway.
        */}
        {(Array.isArray(billingData.roomsPrice) ? billingData.roomsPrice : [])
          .slice()
          .sort((a: { roomTypeName: string }, b: { roomTypeName: string }) =>
            a.roomTypeName.localeCompare(b.roomTypeName)
          )
          .map((roomPrice, i) =>
            step === 1 ? (
              <PriceRow
                label={
                  lang === 'fa'
                    ? `${roomPrice.roomTypeName} (${roomPrice.displayRatePlanCode}) ${roomPrice.quantity}x`
                    : `${roomPrice.roomTypeName} (${roomPrice.displayRatePlanCode})  x${roomPrice.quantity}`
                }
                value={roomPrice.price}
                currency={billingData.curruncy}
                key={i}
              />
            ) : (
              <PriceRow
                label={`${roomPrice.roomTypeName} (${roomPrice.displayRatePlanCode})`}
                value={roomPrice.price}
                currency={billingData.curruncy}
                key={i}
              />
            )
          )}
        <PriceRow
          label={getTranslate(`Subtotal Before Discount`)}
          value={billingData.subTotalPrice.subTotalBeforeDiscount}
          currency={billingData.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.middle}>
        {(promotionApplication?.applied ?? []).map((promotion) => (
          <PriceRow
            key={promotion.promotionId}
            label={promotion.name}
            // MINOR units, and labelled by the PROMOTION's currency rather than the booking's.
            // discountMinor is what the promotion service returned and is denominated in the
            // promotion's own currency; the two are normally the same, but where they differ
            // the amount belongs to the promotion, not to the folio it is being applied to.
            minorAmount={promotion.discountMinor}
            currency={promotion.currency}
            isDiscount
          />
        ))}
        <PromotionAllocationRows
          allocations={promotionAllocations}
          currency={billingData.curruncy}
          roomLabel={allocationRoomLabel}
        />
        {/*
          `discount` is the total gross/net difference and includes promotions. The manual
          amount is separately modelled as `additionalDiscount`; rendering `discount` here
          repeated a code discount under the misleading "Manual Discount" label.
        */}
        {billingData.subTotalPrice.additionalDiscount &&
        billingData.subTotalPrice.additionalDiscount > 0 ? (
          <PriceRow
            label={getTranslate(`Manual Discount`)}
            value={billingData.subTotalPrice.additionalDiscount}
            currency={billingData.curruncy}
            isDiscount
          />
        ) : (
          <></>
        )}
        <PriceRow
          label={getTranslate(`Subtotal After Discount`)}
          value={billingData.subTotalPrice.subTotalAfterDiscount}
          currency={billingData.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.bottom}>
        <PriceRow
          label={getTranslate(`Tax & Mandatory Fees`)}
          value={0}
          currency={billingData.curruncy}
        />
        <PriceRow
          label={
            showTaxText
              ? `${getTranslate('Total Amount Payable')}\n${getTranslate(
                  '(With Tax)'
                )}`
              : `${getTranslate('Total Amount Payable')}`
          }
          value={billingData.totalPayable}
          currency={billingData.curruncy}
          bold
        />
      </div>
    </div>
  )
}

export default FinalBillingSummary
